// Shared pure helpers for the Finance pages (list view + monthly report).
// No JSX / no side effects — safe to import anywhere.

export const THAI_MONTHS = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
export const THAI_MONTHS_FULL = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];
// English short months — the app-wide on-screen date format is "27 Jul 2026".
export const EN_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// Format an ISO "yyyy-mm-dd" as "27 Jul 2026" (on-screen standard).
export const fmtDate = (iso) => { if (!iso) return "-"; const [y, m, d] = String(iso).split("-"); return `${Number(d)} ${EN_MONTHS[Number(m) - 1]} ${y}`; };
// Month-year label as "Jul 2026" (on-screen). Thai variants below stay for printed docs.
export const monthLabelEn = (key) => { const [y, m] = key.split("-"); return `${EN_MONTHS[Number(m) - 1]} ${y}`; };

export const fmt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
export const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

export const monthLabel = (key) => {
  const [y, m] = key.split("-");
  return `${THAI_MONTHS[Number(m) - 1]} ${y}`;
};
export const monthLabelFull = (key) => {
  const [y, m] = key.split("-");
  return `${THAI_MONTHS_FULL[Number(m) - 1]} ${y}`;
};

export const INCOME_CATEGORIES = ["ค่าบริการ", "เงินมัดจำ", "ขายสินค้า", "Omise", "Event", "รายได้อื่น"];
export const EXPENSE_CATEGORIES = ["เงินเดือน", "พนักงานรายวัน", "ค่าเช่า", "ซัพพลายเออร์/ผู้รับเหมา", "การตลาด/โฆษณา", "ค่าเดินทาง/ขนส่ง", "อุปกรณ์/วัสดุ", "Software", "Electronics", "Partner Shared", "ค่าส่งพัสดุ/ไปรษณีย์", "ค่าน้ำค่าไฟ", "สวัสดิการ", "ประกันสังคม", "Refund", "คืนเงินสำรองจ่าย", "ภาษี", "ค่าธรรมเนียม", "รายจ่ายอื่น"];
export const NO_CATEGORY = "ไม่ระบุ";
export const CAT_COLORS = ["#3b6ef5", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#06b6d4", "#ef4444", "#84cc16", "#f97316", "#6366f1"];

export const VAT_RATE = 0.07;
export const WHT_RATE_OPTIONS = [1, 2, 3, 5, 10];

// ── Access control ──
// Non-admin user ids allowed into Finance for data entry (restricted view).
export const FINANCE_STAFF_IDS = ["plai"];
export const isFinanceAdmin = (user) => user?.role === "admin";
export const canViewFinance = (user) => isFinanceAdmin(user) || FINANCE_STAFF_IDS.includes(user?.id);
// Non-admin user ids allowed to issue bills (Billing page / tax documents).
export const BILLING_STAFF_IDS = ["plai"];
export const canBill = (user) => isFinanceAdmin(user) || BILLING_STAFF_IDS.includes(user?.id);
// Rows in these categories are hidden from finance-staff (payroll/salary)
export const isPayrollCategory = (c) => {
  const s = (c || "").trim().toLowerCase();
  return s === "เงินเดือน" || s === "payroll";
};

// Globally-unique row id so merging files from different months never collides
export const uid = () => "t" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

// ── CSV parsing ──
export function parseCSV(text) {
  const rows = [];
  let row = [], field = "", inQuotes = false;
  const t = text.replace(/^﻿/, "");
  for (let i = 0; i < t.length; i++) {
    const c = t[i], next = t[i + 1];
    if (inQuotes) {
      if (c === '"' && next === '"') { field += '"'; i++; }
      else if (c === '"') inQuotes = false;
      else field += c;
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ",") { row.push(field); field = ""; }
      else if (c === "\r") { /* ignore */ }
      else if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
      else field += c;
    }
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return rows;
}

const num = (s) => {
  if (s == null) return 0;
  const n = parseFloat(String(s).replace(/[",\s]/g, ""));
  return isNaN(n) ? 0 : n;
};

const parseThaiDate = (s) => {
  if (!s) return null;
  const datePart = String(s).trim().split(/\s+/)[0];
  const m = datePart.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  return { iso: `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`, monthKey: `${yyyy}-${mm.padStart(2, "0")}` };
};

export function extractTransactions(rows) {
  const headerRowIdx = rows.findIndex((r) => r.some((c) => (c || "").includes("วันที่")));
  if (headerRowIdx < 0) throw new Error("ไม่พบหัวตาราง (คอลัมน์ 'วันที่')");
  const header = rows[headerRowIdx].map((h) => (h || "").trim());

  const dateIdx = header.findIndex((h) => h.includes("วันที่"));
  const incomeIdx = header.findIndex((h) => h === "ฝากเงิน");
  const expenseIdx = header.findIndex((h) => h === "ถอนเงิน");
  const descIdx = header.findIndex((h) => h.includes("รายละเอียด"));
  const balanceIdx = header.findIndex((h) => h.includes("ยอดคงเหลือ"));
  const channelIdx = header.findIndex((h) => h.includes("ช่องทาง"));
  const noteIdx = header.findIndex((h) => h.toLowerCase().includes("note"));

  if (dateIdx < 0 || (incomeIdx < 0 && expenseIdx < 0))
    throw new Error("ไม่พบคอลัมน์ 'วันที่' หรือ 'ฝากเงิน/ถอนเงิน'");

  const txns = [];
  for (const r of rows.slice(headerRowIdx + 1)) {
    if (!r || r.length < 2 || !r.some((c) => c && c.trim())) continue;
    const dt = parseThaiDate(r[dateIdx]);
    if (!dt) continue;
    const income = incomeIdx >= 0 ? num(r[incomeIdx]) : 0;
    const expense = expenseIdx >= 0 ? num(r[expenseIdx]) : 0;
    if (income === 0 && expense === 0) continue;
    txns.push({
      id: uid(),
      date: dt.iso,
      monthKey: dt.monthKey,
      income,
      expense,
      desc: descIdx >= 0 ? (r[descIdx] || "").trim() : "",
      balance: balanceIdx >= 0 ? num(r[balanceIdx]) : null,
      channel: channelIdx >= 0 ? (r[channelIdx] || "").trim() : "",
      label: noteIdx >= 0 ? (r[noteIdx] || "").trim() : "",
      category: "",
      hasVat: income > 0,      // VAT-registered: income is VAT-inclusive by default
      vatTouched: false,
      wht: false,
      whtRate: 0,
      whtAmount: 0,
      taxInvoiceSent: false,
      receiptUrl: "",
      receiptName: "",
    });
  }
  if (txns.length === 0) throw new Error("ไม่พบรายการที่อ่านได้ในไฟล์");
  return txns;
}

// Identity of a statement line — used to detect rows already imported.
// Includes the running balance so two identical same-day amounts stay distinct.
export const txnFingerprint = (t) => [
  t.date || "",
  Number(t.income) || 0,
  Number(t.expense) || 0,
  (t.desc || "").trim(),
  t.balance == null ? "" : Number(t.balance),
].join("|");

// Merge an imported file into the existing rows at ROW level: rows already
// present are skipped (keeping their labels/categories/tax/receipts), new rows
// are appended — so a statement re-exported mid-month adds only what's new.
// Duplicate identical lines are matched by count, not just presence.
export function mergeTransactions(existing, incoming) {
  const seen = new Map();
  for (const t of existing) {
    const k = txnFingerprint(t);
    seen.set(k, (seen.get(k) || 0) + 1);
  }
  const added = [];
  let skipped = 0;
  for (const t of incoming) {
    const k = txnFingerprint(t);
    const c = seen.get(k) || 0;
    if (c > 0) { seen.set(k, c - 1); skipped += 1; }
    else added.push(t);
  }
  const merged = [...existing, ...added].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  return { merged, added, skipped, addedMonths: new Set(added.map((t) => t.monthKey)) };
}

// Ensure every row has the expected fields (covers docs from older versions)
export const normalizeRows = (list) => (list || []).map((t, i) => {
  const isIncome = Number(t.income) > 0;
  const vatTouched = t.vatTouched ?? false;
  // The company is VAT-registered, so income is treated as VAT-inclusive by
  // default. The user can untick it (non-sale receipts) — that sets vatTouched.
  const hasVat = isIncome && !vatTouched ? true : (t.hasVat ?? false);
  const wht = t.wht ?? false;
  const base = { ...t, hasVat };
  // Keep both a % rate and an explicit baht amount, in sync.
  let whtRate = Number(t.whtRate) || 0;
  let whtAmount = t.whtAmount;
  if (whtAmount == null) {
    // Legacy rows stored only a rate → derive the amount.
    whtAmount = wht && whtRate > 0 ? whtAmountFromRate(base, whtRate) : 0;
  } else if (!whtRate && wht && whtAmount > 0) {
    // Amount-only rows → derive the effective rate for the % field.
    whtRate = whtRateFromAmount(base, whtAmount);
  }
  return {
    ...t,
    id: t.id ?? String(i),
    label: t.label ?? t.note ?? "",
    category: t.category ?? "",
    hasVat,
    vatTouched,
    wht,
    whtRate,
    whtAmount,
    taxInvoiceSent: t.taxInvoiceSent ?? false,
    receiptUrl: t.receiptUrl ?? "",
    receiptName: t.receiptName ?? "",
  };
});

// Reconstruct tax components from the NET transferred/received amount and the
// actual withheld amount (฿). net = base + vat − wht, so the invoice total
// (gross = base + vat) = net + wht, and base = gross / (1 + VAT). whtAmount is
// the single source of truth for the calculation.
export function taxParts(t) {
  const net = (Number(t.income) > 0 ? Number(t.income) : Number(t.expense)) || 0;
  const wht = t.wht ? (Number(t.whtAmount) || 0) : 0;
  const gross = net + wht;
  const base = t.hasVat ? gross / (1 + VAT_RATE) : gross;
  const vat = t.hasVat ? round2(gross - base) : 0;
  return { net, base: round2(base), vat, wht: round2(wht), gross: round2(gross) };
}

// %  →  ฿ withheld: withholding is computed on the pre-VAT base.
// net = base(1 + vatMul − r)  ⇒  base = net / (1 + vatMul − r),  wht = base·r
export function whtAmountFromRate(t, rate) {
  const net = (Number(t.income) > 0 ? Number(t.income) : Number(t.expense)) || 0;
  const r = (Number(rate) || 0) / 100;
  const divisor = 1 + (t.hasVat ? VAT_RATE : 0) - r;
  const base = divisor > 0 ? net / divisor : net;
  return round2(base * r);
}
// ฿ withheld  →  effective %
export function whtRateFromAmount(t, amount) {
  const net = (Number(t.income) > 0 ? Number(t.income) : Number(t.expense)) || 0;
  const wht = Number(amount) || 0;
  const gross = net + wht;
  const base = t.hasVat ? gross / (1 + VAT_RATE) : gross;
  return base > 0 ? round2((wht / base) * 100) : 0;
}

// Group a month's transactions by category (income & expense), sorted desc
export const catBreakdown = (txns) => {
  const inc = {}, exp = {};
  for (const t of txns || []) {
    const cat = (t.category || "").trim() || NO_CATEGORY;
    if (Number(t.income) > 0) inc[cat] = (inc[cat] || 0) + Number(t.income);
    if (Number(t.expense) > 0) exp[cat] = (exp[cat] || 0) + Number(t.expense);
  }
  const sort = (o) => Object.entries(o).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  return { income: sort(inc), expense: sort(exp) };
};

// Monthly aggregation (rows assumed chronological ascending)
export function buildMonths(rows) {
  const map = {};
  for (const t of rows) {
    const key = t.monthKey;
    if (!key) continue;
    if (!map[key]) map[key] = { key, income: 0, expense: 0, count: 0, closingBalance: null, missingReceipts: 0, txns: [] };
    const m = map[key];
    m.income += Number(t.income) || 0;
    m.expense += Number(t.expense) || 0;
    m.count += 1;
    if ((t.hasVat || t.wht) && !t.receiptUrl) m.missingReceipts += 1;
    if (t.balance != null) m.closingBalance = t.balance;
    m.txns.push(t);
  }
  const sorted = Object.values(map)
    .map((m) => ({ ...m, net: m.income - m.expense }))
    .sort((a, b) => a.key.localeCompare(b.key));
  let cum = 0;
  return sorted.map((m) => { cum += m.net; return { ...m, cumulative: cum }; });
}

// Tax summary over a set of rows (VAT in/out, WHT withheld/deducted)
export function computeTaxSummary(rows) {
  let vatInput = 0, vatOutput = 0, whtWithheld = 0, whtDeducted = 0, tagged = 0, missing = 0;
  let invoicePending = 0;
  for (const t of rows) {
    // Income with VAT needs a tax invoice issued to the customer
    if (Number(t.income) > 0 && t.hasVat && !t.taxInvoiceSent) invoicePending += 1;
    if (!t.hasVat && !t.wht) continue;
    tagged += 1;
    if (!t.receiptUrl) missing += 1;
    const p = taxParts(t);
    if (Number(t.income) > 0) { vatOutput += p.vat; whtDeducted += p.wht; }
    else { vatInput += p.vat; whtWithheld += p.wht; }
  }
  return { vatInput, vatOutput, vatNet: vatOutput - vatInput, whtWithheld, whtDeducted, tagged, missing, invoicePending };
}

// Accounting-style P&L breakdown: amounts EXCLUDE VAT (the company is
// VAT-registered, so VAT collected/paid is not revenue/expense).
export function pnlBreakdown(txns) {
  const inc = {}, exp = {};
  let incomeTotal = 0, expenseTotal = 0, vatOutput = 0, vatInput = 0;
  for (const t of txns || []) {
    const cat = (t.category || "").trim() || NO_CATEGORY;
    const p = taxParts(t);
    if (Number(t.income) > 0) {
      inc[cat] = (inc[cat] || 0) + p.base; incomeTotal += p.base; vatOutput += p.vat;
    } else if (Number(t.expense) > 0) {
      exp[cat] = (exp[cat] || 0) + p.base; expenseTotal += p.base; vatInput += p.vat;
    }
  }
  const sort = (o) => Object.entries(o).map(([name, value]) => ({ name, value: round2(value) })).sort((a, b) => b.value - a.value);
  return {
    income: sort(inc), expense: sort(exp),
    incomeTotal: round2(incomeTotal), expenseTotal: round2(expenseTotal),
    net: round2(incomeTotal - expenseTotal),
    vatOutput: round2(vatOutput), vatInput: round2(vatInput),
  };
}
